import { FC } from 'react';
import {
  ColumnCheckboxGroupFilter,
  ColumnDateFilter,
  ColumnSelectFilter,
  FilterSection,
} from 'components/data-table';
import type { AreaGeometriesFacets, FacetBucket } from './atoms';

interface AreasCatalogFiltersProps {
  facets: AreaGeometriesFacets | undefined;
}

/**
 * Turns a facet's buckets into select options, dropping empty labels so a blank
 * `created_by` doesn't render an unselectable row.
 *
 * @param {FacetBucket[] | undefined} buckets - Facet buckets from the query.
 * @return {Array} Options for a select filter.
 */
const toOptions = (buckets: FacetBucket[] | undefined) =>
  (buckets ?? [])
    .filter((bucket) => bucket.value?.trim())
    .map((bucket) => ({
      value: bucket.id ?? bucket.value,
      label: bucket.value,
    }));

/**
 * Filter panel for the Areas catalog. Column-bound filters read and write
 * TanStack's column-filter state; the content component maps that state onto
 * query params.
 *
 * @param {AreasCatalogFiltersProps} props - The component props.
 * @return {JSX.Element} The filter panel content.
 */
const AreasCatalogFilters: FC<AreasCatalogFiltersProps> = ({ facets }) => (
  <>
    <FilterSection title="Date Filter">
      <ColumnDateFilter columnId="createdAt" label="Created At" />
      <ColumnDateFilter columnId="updatedAt" label="Updated At" />
    </FilterSection>

    <FilterSection title="Area Filters">
      <ColumnCheckboxGroupFilter
        columnId="active"
        options={[{ value: 'true', label: 'Active' }]}
      />

      <ColumnCheckboxGroupFilter
        columnId="shared"
        options={[{ value: 'true', label: 'Shared' }]}
      />

      <ColumnSelectFilter
        columnId="category"
        label="Area Type"
        options={toOptions(facets?.category?.buckets)}
      />

      {/*
        Status offers Active / Inactive, as the v8 mock does — it is the active
        flag, not `area_geometries.status`, which is null on every row with
        nothing writing it (and so is no longer a column at all).

        Bound to the same column as the Active checkbox above, so the two
        are views of one value rather than two controls fighting over it: ticking
        the box reads as "Active" here, choosing Inactive unticks the box, and
        clearing either drops the filter. `ColumnSelectFilter` stores `[value]`,
        the same shape the checkbox group uses, which is what makes that work.
      */}
      <ColumnSelectFilter
        columnId="active"
        label="Status"
        options={[
          { value: 'true', label: 'Active' },
          { value: 'false', label: 'Inactive' },
        ]}
      />

      <ColumnSelectFilter
        columnId="dataSource"
        label="Data Source"
        options={toOptions(facets?.dataSource?.buckets)}
      />
    </FilterSection>
  </>
);

export { AreasCatalogFilters };
